import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Check, Lightning, Coins, ArrowRight, CreditCard, Sparkle, ShieldCheck,
  X, CaretDown, Star, CheckCircle, ArrowsClockwise, Gear
} from '@phosphor-icons/react';
import DashboardLayout from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { useUpgrade } from '../components/UpgradeSystem';
import { pixelPurchase, pixelSubscribe, pixelInitiateCheckout } from '../utils/pixel';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BillingPage() {
  const { user, checkAuth } = useAuth();
  const { setCreditModal } = useUpgrade();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCompare, setShowCompare] = useState(false);
  const [checkingOut, setCheckingOut] = useState(null);
  const [paymentResult, setPaymentResult] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const currentTier = user?.subscription?.tier || 'starter';

  // Load plans and usage
  useEffect(() => {
    (async () => {
      try {
        const [plansRes, usageRes] = await Promise.all([
          axios.get(`${API}/api/pricing/plans`),
          axios.get(`${API}/api/user/usage`),
        ]);
        setPlans(plansRes.data);
        setUsage(usageRes.data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const success = searchParams.get('success');
    const cancelled = searchParams.get('cancelled');

    if (cancelled) {
      toast.info('Checkout cancelled');
      setSearchParams({}, { replace: true });
      return;
    }

    if (sessionId && success) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = useCallback(async (sessionId, attempt = 0) => {
    const maxAttempts = 8;
    setVerifying(true);

    try {
      const { data } = await axios.get(`${API}/api/payments/status/${sessionId}`);

      if (data.payment_status === 'paid') {
        setPaymentResult(data);
        setVerifying(false);
        setSearchParams({}, { replace: true });
        if (data.tier === 'credits') {
          toast.success(`${data.credits_added} credits added to your account!`);
          pixelPurchase(data.amount_paid ? data.amount_paid / 100 : 0, 'USD', `${data.credits_added} Credits`);
        } else {
          toast.success(`Upgraded to ${data.tier} plan!`);
          pixelSubscribe(data.tier, data.amount_paid ? data.amount_paid / 100 : 0);
        }
        // Refresh user data to reflect new credits/tier
        if (checkAuth) checkAuth();
        // Reload usage
        try {
          const usageRes = await axios.get(`${API}/api/user/usage`);
          setUsage(usageRes.data);
        } catch {}
        return;
      }

      if (data.status === 'expired') {
        setVerifying(false);
        setSearchParams({}, { replace: true });
        toast.error('Payment session expired. Please try again.');
        return;
      }

      // Still processing - retry
      if (attempt < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      } else {
        setVerifying(false);
        setSearchParams({}, { replace: true });
        toast.info('Payment is still processing. Your account will be updated shortly.');
      }
    } catch (err) {
      setVerifying(false);
      if (attempt < maxAttempts) {
        setTimeout(() => pollPaymentStatus(sessionId, attempt + 1), 2000);
      }
    }
  }, [searchParams, checkAuth]);

  const handleUpgrade = async (planId) => {
    if (planId === currentTier || planId === 'starter') return;
    setCheckingOut(planId);
    try {
      const packageId = `${planId}_monthly`;
      pixelInitiateCheckout(planId === 'growth' ? 19 : 29);
      const res = await axios.post(`${API}/api/payments/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Checkout failed. Please try again.');
    }
    setCheckingOut(null);
  };

  const handleManageBilling = async () => {
    try {
      const { data } = await axios.post(`${API}/api/payments/portal`);
      if (data.url) window.location.href = data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not open billing portal');
    }
  };

  if (loading || !plans) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <div className="w-6 h-6 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#c8f060', borderTopColor: 'transparent' }} />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto space-y-8 pb-16" data-testid="billing-page">
        {/* Payment verification overlay */}
        {verifying && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(12px)' }}>
            <div className="text-center p-8 rounded-2xl max-w-sm" style={{ background: '#141414', border: '1px solid rgba(200,240,96,0.2)' }}>
              <ArrowsClockwise size={32} className="animate-spin mx-auto mb-4" style={{ color: '#c8f060' }} />
              <h3 className="text-lg font-semibold mb-2" style={{ color: '#f5f0e8' }}>Verifying payment...</h3>
              <p className="text-xs" style={{ color: 'rgba(245,240,232,0.4)' }}>This will only take a moment</p>
            </div>
          </div>
        )}

        {/* Payment success banner */}
        <AnimatePresence>
          {paymentResult && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="flex items-center gap-3 p-4 rounded-xl"
              style={{ background: 'rgba(200,240,96,0.06)', border: '1px solid rgba(200,240,96,0.2)' }}
              data-testid="payment-success-banner"
            >
              <CheckCircle size={24} weight="fill" style={{ color: '#c8f060' }} />
              <div className="flex-1">
                <p className="text-sm font-semibold" style={{ color: '#c8f060' }}>Payment successful!</p>
                <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.5)' }}>
                  {paymentResult.tier === 'credits'
                    ? `${paymentResult.credits_added} credits have been added to your account.`
                    : `Your subscription has been upgraded to the ${paymentResult.tier} plan.`}
                </p>
              </div>
              <button onClick={() => setPaymentResult(null)} className="p-1 rounded hover:bg-white/5">
                <X size={14} style={{ color: 'rgba(245,240,232,0.3)' }} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Header */}
        <div className="text-center">
          <p className="text-[10px] uppercase tracking-[0.25em] mb-2" style={{ color: '#c8f060' }}>Pricing</p>
          <h1 className="text-3xl sm:text-4xl font-semibold" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
            Choose your plan
          </h1>
          <p className="text-sm mt-2 max-w-md mx-auto" style={{ color: 'rgba(245,240,232,0.4)' }}>
            Simple, transparent pricing. Start free, upgrade when you're ready.
          </p>
        </div>

        {/* Current usage bar */}
        {usage && (
          <div className="flex items-center gap-4 flex-wrap justify-center">
            <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
              <Lightning size={14} weight="fill" style={{ color: '#c8f060' }} />
              <span className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>
                Plan: <span className="font-semibold capitalize" style={{ color: '#f5f0e8' }}>{currentTier}</span>
              </span>
            </div>
            {usage.credits && (
              <div className="flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)' }}>
                <Coins size={14} style={{ color: '#f59e0b' }} />
                <span className="text-xs" style={{ color: 'rgba(245,240,232,0.5)' }}>
                  Credits: <span className="font-semibold" style={{ color: '#f5f0e8' }}>{usage.credits.total}</span>
                </span>
              </div>
            )}
            {currentTier !== 'starter' && (
              <button onClick={handleManageBilling} data-testid="manage-billing-btn"
                className="flex items-center gap-1.5 px-4 py-2 rounded-full text-xs transition-all hover:bg-white/5"
                style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.07)', color: 'rgba(245,240,232,0.5)' }}>
                <Gear size={14} /> Manage Billing
              </button>
            )}
          </div>
        )}

        {/* Plan cards — 3 tiers */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {plans.plans.map((plan, i) => {
            const isCurrent = plan.id === currentTier;
            const isPopular = plan.popular;
            const isPaid = plan.price_monthly > 0;
            const isLoading = checkingOut === plan.id;

            return (
              <motion.div
                key={plan.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08 }}
                data-testid={`plan-${plan.id}`}
                className="relative rounded-2xl overflow-hidden flex flex-col"
                style={{
                  background: isPopular ? 'rgba(200,240,96,0.03)' : '#141414',
                  border: isPopular ? '1.5px solid rgba(200,240,96,0.2)' : isCurrent ? '1.5px solid rgba(200,240,96,0.1)' : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                {isPopular && (
                  <div className="absolute top-0 left-0 right-0 flex justify-center">
                    <span className="text-[9px] font-bold uppercase tracking-wider px-4 py-1 rounded-b-lg"
                      style={{ background: '#c8f060', color: '#080808' }}>
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="p-5 flex flex-col flex-1" style={{ paddingTop: isPopular ? '2.5rem' : '1.5rem' }}>
                  <div className="mb-4">
                    <h3 className="text-base font-semibold" style={{ color: '#f5f0e8' }}>{plan.name}</h3>
                    <p className="text-[11px] mt-0.5" style={{ color: 'rgba(245,240,232,0.35)' }}>{plan.tagline}</p>
                  </div>

                  <div className="mb-4">
                    {isPaid ? (
                      <div className="flex items-end gap-1">
                        <span className="text-3xl font-bold" style={{ color: '#c8f060' }}>
                          ${plan.price_monthly}
                        </span>
                        <span className="text-[10px] mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>/month</span>
                      </div>
                    ) : (
                      <div className="flex items-end gap-1">
                        <span className="text-3xl font-bold" style={{ color: '#f5f0e8' }}>Free</span>
                        <span className="text-[10px] mb-1" style={{ color: 'rgba(245,240,232,0.3)' }}>forever</span>
                      </div>
                    )}
                  </div>

                  <div className="mb-4 px-3 py-2 rounded-lg" style={{ background: 'rgba(200,240,96,0.04)', border: '1px solid rgba(200,240,96,0.1)' }}>
                    <span className="text-xs font-semibold" style={{ color: '#c8f060' }}>{plan.monthly_credits} credits</span>
                    <span className="text-[10px] ml-1" style={{ color: 'rgba(245,240,232,0.3)' }}>/month</span>
                  </div>

                  <div className="flex-1 space-y-2 mb-5">
                    {plan.features.map((f, fi) => (
                      <div key={fi} className="flex items-start gap-2">
                        <Check size={12} weight="bold" className="mt-0.5 flex-shrink-0" style={{ color: '#c8f060' }} />
                        <span className="text-[11px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.55)' }}>{f}</span>
                      </div>
                    ))}
                  </div>

                  {isCurrent ? (
                    <div className="w-full py-2.5 rounded-xl text-xs font-medium text-center"
                      style={{ background: 'rgba(200,240,96,0.06)', color: '#c8f060', border: '1px solid rgba(200,240,96,0.15)' }}>
                      Current Plan
                    </div>
                  ) : !isPaid ? (
                    <div className="w-full py-2.5 rounded-xl text-xs font-medium text-center"
                      style={{ background: 'rgba(255,255,255,0.03)', color: 'rgba(245,240,232,0.3)', border: '1px solid rgba(255,255,255,0.06)' }}>
                      Free Forever
                    </div>
                  ) : (
                    <button
                      onClick={() => handleUpgrade(plan.id)}
                      disabled={isLoading}
                      data-testid={`upgrade-${plan.id}`}
                      className="w-full py-2.5 rounded-xl text-xs font-semibold transition-all hover:opacity-90 flex items-center justify-center gap-2"
                      style={{
                        background: isPopular ? '#c8f060' : 'rgba(255,255,255,0.08)',
                        color: isPopular ? '#080808' : '#f5f0e8',
                        border: isPopular ? 'none' : '1px solid rgba(255,255,255,0.08)',
                        opacity: isLoading ? 0.6 : 1,
                      }}>
                      {isLoading ? (
                        <><ArrowsClockwise size={12} className="animate-spin" /> Processing...</>
                      ) : (
                        <>{plan.cta} <ArrowRight size={12} /></>
                      )}
                    </button>
                  )}
                </div>
              </motion.div>
            );
          })}
        </div>

        {/* Need More Credits section */}
        <div className="p-6 rounded-2xl" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center justify-between flex-wrap gap-3 mb-5">
            <div>
              <h3 className="text-base font-semibold" style={{ color: '#f5f0e8' }}>Need More Credits?</h3>
              <p className="text-xs mt-0.5" style={{ color: 'rgba(245,240,232,0.35)' }}>
                Purchased credits never expire. Top up anytime for videos, carousels, characters and more.
              </p>
            </div>
            <button onClick={() => setCreditModal(true)} data-testid="buy-credits-btn"
              className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold transition-all hover:opacity-90"
              style={{ background: '#c8f060', color: '#080808' }}>
              <Coins size={14} weight="bold" /> Buy Credits
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {plans.credit_packs.map(p => (
              <div key={p.id} className="p-4 rounded-xl relative"
                style={{
                  background: p.best_value ? 'rgba(200,240,96,0.04)' : 'rgba(255,255,255,0.02)',
                  border: p.best_value ? '1px solid rgba(200,240,96,0.15)' : '1px solid rgba(255,255,255,0.05)',
                }}>
                {p.best_value && (
                  <span className="absolute -top-2 right-3 text-[8px] font-bold uppercase px-2 py-0.5 rounded-full"
                    style={{ background: '#c8f060', color: '#080808' }}>Best Value</span>
                )}
                {p.popular && (
                  <span className="absolute -top-2 right-3 text-[8px] font-bold uppercase px-2 py-0.5 rounded-full"
                    style={{ background: 'rgba(200,240,96,0.15)', color: '#c8f060' }}>Popular</span>
                )}
                <p className="text-sm font-semibold mb-0.5" style={{ color: '#f5f0e8' }}>{p.name}</p>
                <p className="text-lg font-bold mb-1" style={{ color: '#f5f0e8' }}>${p.price}</p>
                <p className="text-[10px] mb-2" style={{ color: 'rgba(245,240,232,0.3)' }}>
                  {p.credits} credits &middot; ${p.per_credit}/credit
                </p>
                <p className="text-[10px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.25)' }}>{p.description}</p>
              </div>
            ))}
          </div>

          {plans.credit_costs && (
            <div className="mt-5 pt-4" style={{ borderTop: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-[10px] font-bold uppercase tracking-wider mb-3" style={{ color: 'rgba(245,240,232,0.2)' }}>Credit usage</p>
              <div className="flex flex-wrap gap-x-6 gap-y-1.5">
                {Object.values(plans.credit_costs).map(c => (
                  <div key={c.label} className="flex items-center gap-1.5">
                    <span className="text-[10px] font-semibold" style={{ color: '#c8f060' }}>{c.cost}</span>
                    <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.3)' }}>{c.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Compare All Features */}
        <div className="rounded-2xl overflow-hidden" style={{ background: '#141414', border: '1px solid rgba(255,255,255,0.06)' }}>
          <button onClick={() => setShowCompare(!showCompare)} data-testid="compare-features-btn"
            className="w-full flex items-center justify-between p-5 text-left"
            style={{ color: '#f5f0e8' }}>
            <span className="text-sm font-semibold">Compare All Features</span>
            <CaretDown size={16} style={{ transform: showCompare ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s', color: 'rgba(245,240,232,0.3)' }} />
          </button>
          <AnimatePresence>
            {showCompare && (
              <motion.div initial={{ height: 0 }} animate={{ height: 'auto' }} exit={{ height: 0 }} className="overflow-hidden">
                <CompareTable />
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* FAQ */}
        <div className="space-y-3">
          <h3 className="text-base font-semibold text-center mb-4" style={{ color: '#f5f0e8', fontFamily: "'Instrument Serif', serif" }}>
            Frequently Asked Questions
          </h3>
          {[
            { q: 'Can I cancel anytime?', a: 'Yes. Cancel your subscription at any time with no penalties. Your plan stays active until the end of your billing period.' },
            { q: 'What happens to my credits if I downgrade?', a: 'Purchased credits never expire. Monthly credits reset with each billing cycle.' },
            { q: 'Do credits expire?', a: 'Purchased credits never expire. Monthly credits reset on your billing date.' },
            { q: 'What can I do on the free plan?', a: 'Use Copy Studio, Content Studio, Daily Plans, and create limited flyers and carousels with watermark. Upgrade to download or use Video Ads.' },
          ].map((faq, i) => (
            <div key={i} className="p-4 rounded-xl" data-testid={`faq-${i}`}
              style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
              <p className="text-xs font-semibold mb-1.5" style={{ color: '#f5f0e8' }}>{faq.q}</p>
              <p className="text-[11px] leading-relaxed" style={{ color: 'rgba(245,240,232,0.4)' }}>{faq.a}</p>
            </div>
          ))}
        </div>

        {/* Trust */}
        <div className="text-center py-4">
          <div className="flex items-center justify-center gap-2 mb-2">
            <ShieldCheck size={18} style={{ color: '#c8f060' }} />
            <h3 className="text-sm font-semibold" style={{ color: '#f5f0e8' }}>No free trial needed</h3>
          </div>
          <p className="text-[11px] max-w-md mx-auto" style={{ color: 'rgba(245,240,232,0.3)' }}>
            Start free. Upgrade only when you're ready. No pressure, no hidden charges.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
}

/* Feature Comparison Table */
function CompareTable() {
  const features = [
    { name: 'Monthly Credits', starter: '20', growth: '50', pro: '100' },
    { name: 'Daily Growth Plan', starter: true, growth: true, pro: true },
    { name: 'Copy Studio', starter: true, growth: true, pro: true },
    { name: 'Content Studio', starter: true, growth: true, pro: true },
    { name: 'Marketing Coach', starter: 'Limited', growth: 'Full', pro: 'Full' },
    { name: 'Flyer Creator', starter: 'Limited', growth: 'Full', pro: 'Full' },
    { name: 'Carousel Creator', starter: 'Limited', growth: 'Full', pro: 'Full' },
    { name: 'Template Studio', starter: false, growth: '15 templates', pro: '15 templates' },
    { name: 'Character Creator', starter: false, growth: true, pro: 'Higher usage' },
    { name: 'Video Ad Creator', starter: false, growth: false, pro: 'Up to 12s' },
    { name: 'Landing Page Builder', starter: false, growth: true, pro: true },
    { name: 'Batch Create', starter: false, growth: true, pro: true },
    { name: 'Meta Ads Manager', starter: false, growth: false, pro: true },
    { name: 'Download & Export', starter: false, growth: true, pro: true },
    { name: 'Watermark-free', starter: false, growth: true, pro: true },
    { name: 'Priority Queue', starter: false, growth: false, pro: true },
  ];

  const renderCell = (val) => {
    if (val === true) return <Check size={14} weight="bold" style={{ color: '#c8f060' }} />;
    if (val === false) return <X size={14} style={{ color: 'rgba(245,240,232,0.12)' }} />;
    return <span className="text-[10px]" style={{ color: 'rgba(245,240,232,0.5)' }}>{val}</span>;
  };

  return (
    <div className="overflow-x-auto px-5 pb-5" data-testid="compare-table">
      <table className="w-full text-xs">
        <thead>
          <tr>
            <th className="text-left py-2 pr-4 font-semibold" style={{ color: 'rgba(245,240,232,0.3)', minWidth: '160px' }}>Feature</th>
            {['Starter', 'Growth', 'Pro'].map(p => (
              <th key={p} className="text-center py-2 px-3 font-semibold" style={{ color: '#f5f0e8' }}>{p}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {features.map((f, i) => (
            <tr key={i} style={{ borderTop: '1px solid rgba(255,255,255,0.03)' }}>
              <td className="py-2.5 pr-4" style={{ color: 'rgba(245,240,232,0.45)' }}>{f.name}</td>
              {['starter', 'growth', 'pro'].map(p => (
                <td key={p} className="text-center py-2.5 px-3">{renderCell(f[p])}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
